<script setup lang="ts">
const { trimDescription, addSuffix } = useMeta()
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute('slug___cs')

const slug = computed(() => route.params.slug as string)

const productsRepository = createProductsRepository()

const {
  data: line,
  error,
  status: lineStatus,
} = await useAsyncData(
  'product-lines-line' + slug,
  () => productsRepository.getLineBySlug(slug.value),
  {
    watch: [slug],
  }
)

if (line.value?.translationOfNid) {
  await useTranslatedSlugs(line.value?.translationOfNid)
}

const nuxtApp = useNuxtApp()

watchEffect(async () => {
  await nuxtApp.runWithContext(async () => {
    if (error.value) {
      const { data: translatedSlug } = await useAsyncData(() =>
        productsRepository.translateLineSlug(slug.value)
      )

      if (translatedSlug.value) {
        navigateTo(
          localePath({
            name: 'product-lines-slug',
            params: { slug: translatedSlug.value.urlSlug },
          })
        )
      } else {
        throw createError({ statusCode: 404, message: t('error.pageNotFound') })
      }
    }
  })
})

const userStore = useUser()
const { isLoggedIn, userCustomerGroupId } = storeToRefs(userStore)

const userConfigStore = useUserConfig()
const { customerGroupId, priceCategoryId } = storeToRefs(userConfigStore)

const { data: productsSetsData } = await useLazyAsyncData(
  () =>
    createProductSetsRepository().getAll({
      filter: APIFilters.makeFilter([
        {
          [APIFilterOP.EQUALS]: {
            'productLine.nid': line.value?.nid,
          },
        },
        {
          [APIFilterOP.EQUALS]: {
            hideInProductLineListing: 0,
          },
        },
      ]),
      sort: APIFilters.makeSort({ weight: 'ASC' }),
      ...((customerGroupId.value || userCustomerGroupId.value) && {
        customerGroupNid: customerGroupId.value ?? userCustomerGroupId.value,
      }),
      ...(priceCategoryId.value && {
        priceCategoryNid: priceCategoryId.value,
      }),
    }),
  {
    watch: [
      line,
      isLoggedIn,
      userCustomerGroupId,
      customerGroupId,
      priceCategoryId,
    ],
  }
)

const productsSets = computed(() => productsSetsData.value?.items ?? [])

const designersRepository = createDesignersRepository()
const { data: designerData, status: designerStatus } = await useLazyAsyncData(
  () => designersRepository.getByLine(line.value?.title),
  {
    watch: [line],
  }
)

const designer = computed(() => designerData.value?.items[0] || undefined)

const { defaultOgImage } = useOgImage()

watchEffect(() => {
  useSeoMeta({
    title: addSuffix(line.value?.title) ?? t('pages.product-lines.meta.title'),
    description:
      trimDescription(stripHtml(line.value?.metaDescription || '')) ??
      t('pages.index.meta.description'),
    ogImage: line.value?.backgroundImages[0]?.url
      ? withHost(line.value?.backgroundImages[0]?.url + '/1200x1200/FIT')
      : defaultOgImage,
  })
})
</script>

<template>
  <div>
    <div v-if="lineStatus === 'pending'"></div>
    <div v-if="lineStatus === 'error'"></div>
    <div v-if="lineStatus === 'success' && line">
      <div class="container mb-5 py-4 sm:pb-10 xl:py-10">
        <UiBreadcrumbs
          :slug="line?.title ?? undefined"
          :overrides="[
            undefined,
            {
              label: t('breadcrumb.items.product-lines.label'),
              to: localePath({ name: 'product-lines' }),
            },
          ]"
          :append="[
            {
              label: line?.title,
              to: route.path,
            },
          ]"
          rounded
          class="mb-5 sm:mb-10"
        />
        <div class="flex flex-wrap justify-between gap-x-10">
          <p
            class="font-pragmatica-ext ml-10 pb-5 text-6xl font-bold first-letter:-ml-10 md:ml-16 md:text-8xl md:first-letter:-ml-16 lg:w-2/5"
          >
            {{ line?.title }}
          </p>
          <div class="max-w-xl">
            <div
              v-if="designer"
              class="font-pragmatica-ext pb-5 text-3xl font-bold"
              v-html="$t('base.designers.designedBy') + ' ' + designer?.title"
            ></div>
            <p>
              {{ stripHtml(line?.description || '') }}
            </p>
            <NuxtLink
              class="mt-5 block w-fit"
              :to="{ path: route.path, hash: '#' + 'products' }"
            >
              <UiButtonBlack>
                {{ $t('productline.hero.cta') }}
              </UiButtonBlack>
            </NuxtLink>
          </div>
        </div>
      </div>
      <UiHeroCarousel v-if="line" :line="line" />
      <div id="lineContent" class="relative">
        <ProductLinesProductLineTabMenu
          class="sticky top-0 z-40"
          :line-name="line?.title"
        />
        <ProductLinesProductLineWidget
          v-for="widget in line?.widgets"
          :widget="widget"
        />
        <ProductLinesProductLineDesignerCard
          v-if="designerStatus !== 'pending' && designer"
          :designer="designer"
          :line="line"
        />
        <ProductLinesProductLineProductsGrid
          id="products"
          :line-title="line?.title || ''"
          :sets="productsSets"
          :fabric-description="line?.fabricDescription || ''"
          :footer-description="line?.footerDescription || ''"
        />
        <ProductLinesProductLineReferences id="references" :line="line" />
        <ProductLinesProductLineNews id="news" :line="line" />
        <ProductLinesProductLineGallery id="gallery" :line="line" />
        <ProductLinesProductLineDownload id="files" :line="line" />
      </div>
    </div>
  </div>
</template>

<style>
.dropdown-lines {
  width: 224px;
  max-height: 400px;
  overflow-y: scroll;
  box-shadow: none !important;
}
</style>
